import { NavLink, Outlet } from 'react-router-dom'
import {
  Boxes,
  FileWarning,
  FolderTree,
  LayoutDashboard,
  LogOut,
  MessageSquareWarning,
  Ruler,
  Scale,
  Users,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/features/auth/useAuth'
import { useNewFeedbackCount } from '@/features/feedback/useFeedback'
import { useLastCheck } from '@/features/inconsistencies/last-check'
import { cn } from '@/lib/utils'

const NAV_ITEMS = [
  { to: '/', label: 'Обзор', icon: LayoutDashboard },
  { to: '/materials', label: 'Пункты затрат', icon: Boxes },
  { to: '/categories', label: 'Категории', icon: FolderTree },
  { to: '/units', label: 'Единицы измерения', icon: Scale },
  { to: '/rules', label: 'Правила расчёта', icon: Ruler },
  { to: '/inconsistencies', label: 'Несоответствия', icon: FileWarning },
  { to: '/feedback', label: 'Сообщения', icon: MessageSquareWarning },
  { to: '/users', label: 'Пользователи', icon: Users },
] as const

/** Sidebar + content. The sidebar is the map of the console; pages arrive stage by stage. */
export function AppShell() {
  const { user, profile, signOut } = useAuth()
  // Unreviewed "Tell us" messages: the reason to open the console on most days.
  const newFeedbackCount = useNewFeedbackCount()
  // Errors found by the last run of "Несоответствия" (stored locally; the page refreshes it).
  const lastCheck = useLastCheck()

  return (
    <div className="flex min-h-svh">
      <aside className="bg-sidebar text-sidebar-foreground border-sidebar-border hidden w-64 shrink-0 flex-col border-r md:flex">
        <div className="flex h-16 items-center gap-2 px-5 font-semibold">
          <span className="bg-primary text-primary-foreground flex size-8 items-center justify-center rounded-lg text-sm">
            EP
          </span>
          Estimate Pro
        </div>
        <nav className="flex flex-col gap-1 px-3 py-2">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )
              }
            >
              <Icon className="size-4" />
              {label}
              {to === '/feedback' && newFeedbackCount > 0 ? (
                <Badge className="ml-auto">{newFeedbackCount}</Badge>
              ) : null}
              {to === '/inconsistencies' && lastCheck && lastCheck.errors > 0 ? (
                <Badge
                  variant="destructive"
                  className="ml-auto"
                  title={`Ошибок по последней проверке (${new Date(lastCheck.at).toLocaleString('ru-RU')}); откройте раздел, чтобы обновить`}
                >
                  {lastCheck.errors}
                </Badge>
              ) : null}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto border-t px-5 py-4 text-xs">
          <div className="text-foreground truncate font-medium">
            {profile?.displayName ?? user?.email}
          </div>
          <div className="text-muted-foreground mt-1 flex items-center gap-2">
            <Badge variant="secondary">админ</Badge>
          </div>
          <Button variant="ghost" size="sm" className="mt-3 w-full" onClick={() => void signOut()}>
            <LogOut />
            Выйти
          </Button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 items-center justify-between border-b px-6 md:hidden">
          <span className="font-semibold">Estimate Pro</span>
          <Button variant="ghost" size="icon" onClick={() => void signOut()} aria-label="Выйти">
            <LogOut />
          </Button>
        </header>
        <main className="min-w-0 flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
